import React, { useState, useEffect } from 'react';
import api from '../../api/axios';
import DataTable from '../Common/DataTable';

export default function TeacherClasses() {
  const [myClass, setMyClass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rollMap, setRollMap] = useState({});

  useEffect(() => { fetchClass(); }, []);

  const fetchClass = async () => {
    setLoading(true);
    try {
      const res = await api.get('/teacher/my-class');
      setMyClass(res.data);
      const map = {};
      (res.data.students || []).forEach(s => {
        map[s.student_id] = s.roll_no || '';
      });
      setRollMap(map);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSaveAllRolls = async () => {
    setSaving(true);
    try {
      const assignments = Object.entries(rollMap)
        .filter(([_, rn]) => rn !== '' && rn !== null && rn !== undefined)
        .map(([id, rn]) => ({ student_id: parseInt(id), roll_no: parseInt(rn) }));

      if (assignments.length === 0) {
        alert('Please enter at least one roll number');
        setSaving(false);
        return;
      }

      const res = await api.post('/students/assign-roll-numbers', assignments);
      if (res.data.errors && res.data.errors.length > 0) {
        alert(`Saved ${res.data.updated}. Errors:\n${res.data.errors.join('\n')}`);
      } else {
        alert(`✅ Saved ${res.data.updated} roll number(s)`);
      }
      fetchClass();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleAutoFill = () => {
    if (!myClass) return;
    const sorted = [...myClass.students].sort((a, b) => a.name.localeCompare(b.name));
    const newMap = {};
    sorted.forEach((s, i) => { newMap[s.student_id] = i + 1; });
    setRollMap(newMap);
  };

  if (loading) return <div className="bg-white p-6 rounded shadow text-center text-gray-500">Loading...</div>;

  if (!myClass?.class_name) {
    return (
      <div className="bg-white p-8 rounded shadow text-center">
        <i className="fas fa-chalkboard text-4xl text-gray-300 mb-3"></i>
        <p className="text-gray-500">You are not assigned to any class yet.</p>
      </div>
    );
  }

  const assignedCount = Object.values(rollMap).filter(v => v !== '' && v !== null).length;
  const totalCount = myClass.total_students;
  const allAssigned = assignedCount === totalCount;

  const columns = ['Roll #', 'Registration #', 'Name', 'Gender', 'Attendance', 'Grade', 'Fee Status'];
  const data = myClass.students.map(s => ({
    'Roll #': (
      <input
        type="number"
        min="1"
        className="w-16 p-1 border rounded text-center text-sm font-bold bg-blue-50 focus:bg-white"
        value={rollMap[s.student_id] || ''}
        onChange={(e) => setRollMap({ ...rollMap, [s.student_id]: e.target.value })}
        placeholder="#"
      />
    ),
    'Registration #': (
      <span className="text-xs text-gray-600 font-mono">{s.registration_number || '—'}</span>
    ),
    Name: s.name,
    Gender: s.gender,
    Attendance: (
      <span className={`px-2 py-1 rounded text-xs font-semibold ${
        s.attendance_percent >= 90 ? 'bg-green-100 text-green-800'
        : s.attendance_percent >= 75 ? 'bg-yellow-100 text-yellow-800'
        : 'bg-red-100 text-red-800'
      }`}>{s.attendance_percent}%</span>
    ),
    Grade: s.letter_grade,
    'Fee Status': (
      <span className={`px-2 py-1 rounded text-xs font-semibold ${
        s.fees_paid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}>{s.fees_paid ? 'Paid' : `$${s.pending_fees.toFixed(0)}`}</span>
    ),
  }));

  return (
    <div>
      <div className="bg-white p-5 rounded shadow mb-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">📚 My Class: {myClass.class_name}</h1>
            <p className="text-sm text-gray-500 mt-1">
              <i className="fas fa-building"></i> {myClass.campus_name}
              {' · '}
              <i className="fas fa-users"></i> {totalCount} students
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <button onClick={handleAutoFill}
              className="px-3 py-2 text-sm bg-purple-100 text-purple-700 rounded hover:bg-purple-200">
              <i className="fas fa-magic mr-1"></i> Auto-fill (A-Z)
            </button>
            <button onClick={handleSaveAllRolls} disabled={saving}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving...' : <><i className="fas fa-save mr-1"></i> Save Roll Numbers</>}
            </button>
          </div>
        </div>

        {/* Progress */}
        <div className="mt-4">
          <div className="flex justify-between items-center text-xs text-gray-500 mb-1">
            <span>Roll Numbers Assigned: {assignedCount} / {totalCount}</span>
            {allAssigned ? (
              <span className="text-green-600 font-semibold">
                <i className="fas fa-check-circle"></i> All Assigned
              </span>
            ) : (
              <span className="text-yellow-600 font-semibold">
                <i className="fas fa-exclamation-circle"></i> {totalCount - assignedCount} pending
              </span>
            )}
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${allAssigned ? 'bg-green-500' : 'bg-blue-500'}`}
              style={{ width: `${(assignedCount / totalCount) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <div className="bg-white p-4 rounded shadow">
        <h2 className="text-lg font-semibold mb-3">👨‍🎓 Assign Roll Numbers</h2>
        <p className="text-xs text-gray-500 mb-3">
          <i className="fas fa-info-circle"></i> Enter roll numbers per class — must be unique within this class.
          Click "Auto-fill (A-Z)" to assign 1, 2, 3... then adjust as needed.
        </p>
        <DataTable columns={columns} data={data} />
      </div>
    </div>
  );
}